import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Badge } from './ui/badge';
import { useAuth } from '../contexts/AuthContext';
import { User, Building, MapPin, Globe, DollarSign, Clock, Plus, X, Upload, Camera, Trash2 } from 'lucide-react';
import axios from 'axios';
import PhotoUpload from './PhotoUpload';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const ProfileModal = ({ isOpen, onClose }) => {
  const { user, setUser, logout } = useAuth();
  
  // Debug logging
  React.useEffect(() => {
    console.log('ProfileModal props changed:', { isOpen, hasUser: !!user });
  }, [isOpen, user]);
  const [loading, setLoading] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [profileData, setProfileData] = useState({
    name: '',
    bio: '',
    business_name: '',
    services_provided: [],
    contact_phone: '',
    contact_email: '',
    location: '',
    website: '',
    years_experience: '',
    hourly_rate: '',
    availability: 'Available',
    profile_picture: '',
    portfolio_images: []
  });
  const [newService, setNewService] = useState('');

  const handleProfilePictureUpload = (photoUrl) => {
    console.log('Uploading profile picture:', photoUrl);
    setProfileData(prev => {
      const newData = {
        ...prev,
        profile_picture: photoUrl
      };
      console.log('Updated profile data after profile picture:', newData);
      return newData;
    });
  };

  const handlePortfolioUpload = (photoUrls) => {
    console.log('Uploading portfolio images:', photoUrls);
    setProfileData(prev => {
      // Properly filter out empty, null, or undefined values
      let cleanedUrls = [];
      
      if (Array.isArray(photoUrls)) {
        cleanedUrls = photoUrls.filter(url => url && url.trim() !== '');
      } else if (photoUrls && typeof photoUrls === 'string' && photoUrls.trim() !== '') {
        cleanedUrls = [photoUrls];
      }
      
      const newData = {
        ...prev,
        portfolio_images: cleanedUrls
      };
      console.log('Updated profile data after portfolio:', newData);
      console.log('Cleaned portfolio URLs:', cleanedUrls);
      console.log('New portfolio count:', cleanedUrls.length);
      return newData;
    });
  };

  const handleDeleteProfile = async () => {
    if (!window.confirm('Are you sure you want to delete your profile? This action cannot be undone and will remove all your data including friends, reviews, and referrals.')) {
      return;
    }

    setLoading(true);
    try {
      const token = localStorage.getItem('access_token');
      const response = await axios.delete(`${API_URL}/api/profile/delete`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (response.data.status === 'success') {
        alert('Profile deleted successfully. You will be logged out.');
        await logout(); // Log out the user
        onClose(); // Close the modal
      } else {
        alert('Failed to delete profile: ' + response.data.message);
      }
    } catch (error) {
      console.error('Error deleting profile:', error);
      alert('Failed to delete profile: ' + (error.response?.data?.detail || error.message));
    }
    setLoading(false);
  };

  // Function to validate and clean portfolio images
  const validatePortfolioImages = async (imageUrls) => {
    if (!imageUrls || !Array.isArray(imageUrls)) return [];
    
    const validImages = [];
    
    for (const url of imageUrls) {
      if (!url || typeof url !== 'string' || url.trim() === '') continue;
      
      try {
        // Check if image exists by making a HEAD request
        const response = await fetch(`${API_URL}${url}`, { method: 'HEAD' });
        if (response.ok) {
          validImages.push(url);
        } else {
          console.log(`❌ Invalid portfolio image removed: ${url}`);
        }
      } catch (error) {
        console.log(`❌ Portfolio image validation failed: ${url}`, error);
      }
    }
    
    console.log(`✅ Portfolio validation: ${validImages.length}/${imageUrls.length} images valid`);
    return validImages;
  };

  useEffect(() => {
    const loadAndValidateProfile = async () => {
      if (user && isOpen) {
        const initialPortfolioImages = (user.portfolio_images || []).filter(img => img && img.trim() !== '');
        console.log('Initial portfolio images from user:', initialPortfolioImages);
        console.log('Initial portfolio count:', initialPortfolioImages.length);
        
        // Filter out invalid file URLs (404 errors) but keep base64 data URLs
        const validPortfolioImages = initialPortfolioImages.filter(img => {
          // Keep base64 data URLs (they work)
          if (img.startsWith('data:')) {
            return true;
          }
          // Remove file URLs that are likely 404ing (old format)
          if (img.startsWith('/api/images/')) {
            console.log(`🗑️ Removing potentially invalid file URL: ${img}`);
            return false;
          }
          // Keep other formats
          return true;
        });
        
        console.log(`📊 Portfolio cleanup: ${initialPortfolioImages.length} → ${validPortfolioImages.length} images`);
        
        const newProfileData = {
          name: user.name || '',
          contact_phone: user.contact_phone || '',
          contact_email: user.contact_email || user.email || '',
          business_name: user.business_name || '',
          services_provided: user.services_provided || [],
          location: user.location || '',
          years_experience: user.years_experience || '',
          hourly_rate: user.hourly_rate || '',
          bio: user.bio || '',
          availability: user.availability || 'Available',
          profile_picture: user.profile_picture || '',
          portfolio_images: validPortfolioImages
        };
        console.log('Final profile data after validation:', newProfileData);
        console.log('Final portfolio count:', validPortfolioImages.length);
        setProfileData(newProfileData);
      }
    };
    
    loadAndValidateProfile();
  }, [user, isOpen]);

  const handleInputChange = (field, value) => {
    setProfileData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const addService = () => {
    if (newService.trim() && !profileData.services_provided.includes(newService.trim())) {
      setProfileData(prev => ({
        ...prev,
        services_provided: [...prev.services_provided, newService.trim()]
      }));
      setNewService('');
    }
  };

  const removeService = (serviceToRemove) => {
    setProfileData(prev => ({
      ...prev,
      services_provided: prev.services_provided.filter(service => service !== serviceToRemove)
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const updateData = {
        ...profileData,
        years_experience: profileData.years_experience ? parseInt(profileData.years_experience) : null
      };
      
      console.log('Submitting profile data:', updateData);

      const response = await axios.put(`${API_URL}/api/profile-simple`, updateData);
      
      console.log('Profile update response:', response.data);
      
      if (response.data.status === 'success') {
        // Update user context with the returned updated user data
        if (response.data.user) {
          console.log('Updating user context with:', response.data.user);
          setUser(response.data.user);
        }
        onClose();
        alert('Profile updated successfully!');
      }
    } catch (error) {
      console.error('Error updating profile:', error);
      alert('Failed to update profile. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <User className="w-5 h-5" style={{color: 'var(--brand-navy)'}} />
            Professional Profile
          </DialogTitle>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Basic Information */}
          <div className="space-y-4">
            <h3 className="text-lg font-semibold" style={{color: 'var(--brand-navy)'}}>
              Basic Information
            </h3>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="name">Full Name *</Label>
                <Input
                  id="name"
                  value={profileData.name}
                  onChange={(e) => handleInputChange('name', e.target.value)}
                  required
                  className="mt-1"
                />
              </div>
              
              <div>
                <Label htmlFor="business_name">Business Name</Label>
                <Input
                  id="business_name"
                  value={profileData.business_name}
                  onChange={(e) => handleInputChange('business_name', e.target.value)}
                  className="mt-1"
                  placeholder="Your company or business name"
                />
              </div>
            </div>

            <div>
              <Label htmlFor="bio">Professional Bio</Label>
              <Textarea
                id="bio"
                value={profileData.bio}
                onChange={(e) => handleInputChange('bio', e.target.value)}
                className="mt-1"
                rows={3}
                placeholder="Tell others about your expertise and experience..."
              />
            </div>
          </div>

          {/* Services Provided */}
          <div className="space-y-4">
            <h3 className="text-lg font-semibold" style={{color: 'var(--brand-navy)'}}>
              Services You Provide
            </h3>
            
            <div className="flex gap-2">
              <Input
                value={newService}
                onChange={(e) => setNewService(e.target.value)}
                placeholder="Add a service (e.g., HVAC, Plumbing)"
                className="flex-1"
                onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addService())}
              />
              <Button type="button" onClick={addService} variant="outline">
                <Plus className="w-4 h-4" />
              </Button>
            </div>
            
            {profileData.services_provided.length > 0 && (
              <div className="flex flex-wrap gap-2">
                {profileData.services_provided.map(service => (
                  <Badge
                    key={service}
                    variant="secondary"
                    className="bg-blue-100 text-blue-800 flex items-center gap-1"
                  >
                    {service}
                    <button
                      type="button"
                      onClick={() => removeService(service)}
                      className="hover:bg-blue-200 rounded-full p-0.5"
                    >
                      <X className="w-3 h-3" />
                    </button>
                  </Badge>
                ))}
              </div>
            )}
          </div>

          {/* Contact Information */}
          <div className="space-y-4">
            <h3 className="text-lg font-semibold" style={{color: 'var(--brand-navy)'}}>
              Contact Information
            </h3>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="contact_phone">Phone Number</Label>
                <Input
                  id="contact_phone"
                  value={profileData.contact_phone}
                  onChange={(e) => handleInputChange('contact_phone', e.target.value)}
                  className="mt-1"
                  placeholder="+1 (555) 123-4567"
                />
              </div>
              
              <div>
                <Label htmlFor="contact_email">Contact Email</Label>
                <Input
                  id="contact_email"
                  type="email"
                  value={profileData.contact_email}
                  onChange={(e) => handleInputChange('contact_email', e.target.value)}
                  className="mt-1"
                />
              </div>
              
              <div>
                <Label htmlFor="location">Location</Label>
                <Input
                  id="location"
                  value={profileData.location}
                  onChange={(e) => handleInputChange('location', e.target.value)}
                  className="mt-1"
                  placeholder="City, State"
                />
              </div>
              
              <div>
                <Label htmlFor="website">Website</Label>
                <Input
                  id="website"
                  value={profileData.website}
                  onChange={(e) => handleInputChange('website', e.target.value)}
                  className="mt-1"
                  placeholder="https://yourwebsite.com"
                />
              </div>
            </div>
          </div>

          {/* Professional Details */}
          <div className="space-y-4">
            <h3 className="text-lg font-semibold" style={{color: 'var(--brand-navy)'}}>
              Professional Details
            </h3>
            
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <Label htmlFor="years_experience">Years of Experience</Label>
                <Input
                  id="years_experience"
                  type="number"
                  min="0"
                  max="50"
                  value={profileData.years_experience}
                  onChange={(e) => handleInputChange('years_experience', e.target.value)}
                  className="mt-1"
                />
              </div>
              
              <div>
                <Label htmlFor="hourly_rate">Rate (Optional)</Label>
                <Input
                  id="hourly_rate"
                  value={profileData.hourly_rate}
                  onChange={(e) => handleInputChange('hourly_rate', e.target.value)}
                  className="mt-1"
                  placeholder="$100/hr or $500 project"
                />
              </div>
              
              <div>
                <Label htmlFor="availability">Availability</Label>
                <Select onValueChange={(value) => handleInputChange('availability', value)}>
                  <SelectTrigger className="mt-1">
                    <SelectValue placeholder={profileData.availability || "Available"} />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Available">Available</SelectItem>
                    <SelectItem value="Busy">Busy</SelectItem>
                    <SelectItem value="Unavailable">Unavailable</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>

          {/* Profile Picture */}
          <div className="space-y-4">
            <h3 className="text-lg font-semibold" style={{color: 'var(--brand-navy)'}}>
              Profile Picture
            </h3>
            
            <PhotoUpload
              currentPhotoUrl={profileData.profile_picture}
              onPhotoUploaded={handleProfilePictureUpload}
              maxPhotos={1}
              label=""
              className="max-w-sm"
            />
          </div>

          {/* Portfolio Gallery */}
          <div className="space-y-4">
            <h3 className="text-lg font-semibold" style={{color: 'var(--brand-navy)'}}>
              Work Portfolio
            </h3>
            
            <PhotoUpload
              currentPhotoUrl={profileData.portfolio_images}
              onPhotoUploaded={handlePortfolioUpload}
              maxPhotos={6}
              label=""
              className=""
            />
          </div>

          {/* Action Buttons */}
          <div className="space-y-3 pt-4 border-t">
            <div className="flex gap-2">
              <Button
                type="button"
                variant="outline"
                onClick={onClose}
                className="flex-1"
              >
                Cancel
              </Button>
              <Button
                type="submit"
                disabled={loading}
                className="flex-1 text-white"
                style={{
                  background: 'linear-gradient(135deg, var(--brand-navy) 0%, var(--brand-navy-dark) 100%)'
                }}
              >
                {loading ? 'Saving...' : 'Save Profile'}
              </Button>
            </div>
            
            {/* Delete Profile Button */}
            <div className="pt-2 border-t">
              <Button
                type="button"
                variant="destructive"
                onClick={handleDeleteProfile}
                disabled={loading}
                className="w-full text-white bg-red-600 hover:bg-red-700"
              >
                <Trash2 className="w-4 h-4 mr-2" />
                {loading ? 'Deleting...' : 'Delete Profile & All Data'}
              </Button>
              <p className="text-xs text-gray-500 text-center mt-1">
                This will permanently delete your profile, friends, reviews, and all associated data
              </p>
            </div>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default ProfileModal;